import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment.ipt.local";
import { AzureCrossTabService } from "./azure-cross-tab.service";

interface TokenRecoveryLock {
	createdAt: number;
	ownerId: string;
}

@Injectable({ providedIn: "root" })
export class TokenRecoveryLockService {
	private readonly key =
		`core-automatizaciones-token-recovery-${environment.AZURE_CLIENT_ID}`;
	private readonly lifetimeMs = 20 * 1000;
	private readonly waitIntervalMs = 250;

	constructor(private crossTabService: AzureCrossTabService) {}

	acquire(): boolean {
		const currentLock = this.get();
		if (
			currentLock &&
			currentLock.ownerId !== this.crossTabService.currentTabId
		) {
			return false;
		}

		localStorage.setItem(
			this.key,
			JSON.stringify({
				createdAt: Date.now(),
				ownerId: this.crossTabService.currentTabId,
			} as TokenRecoveryLock)
		);

		return (
			this.get()?.ownerId === this.crossTabService.currentTabId
		);
	}

	release(force = false): void {
		const lock = this.get();
		if (
			force ||
			lock?.ownerId === this.crossTabService.currentTabId
		) {
			localStorage.removeItem(this.key);
		}
	}

	async wait(): Promise<void> {
		const expiresAt = Date.now() + this.lifetimeMs;
		while (Date.now() < expiresAt && this.get()) {
			await new Promise((resolve) =>
				window.setTimeout(resolve, this.waitIntervalMs)
			);
		}
	}

	private get(): TokenRecoveryLock | null {
		const rawValue = localStorage.getItem(this.key);
		if (!rawValue) {
			return null;
		}

		try {
			const lock = JSON.parse(rawValue) as TokenRecoveryLock;
			if (
				!lock.createdAt ||
				!lock.ownerId ||
				Date.now() - lock.createdAt > this.lifetimeMs
			) {
				localStorage.removeItem(this.key);
				return null;
			}
			return lock;
		} catch {
			localStorage.removeItem(this.key);
			return null;
		}
	}
}
